import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  fetchDrugProducts,
  assignDrugToProducts,
  unassignDrugFromProducts,
  searchProduct as searchProductsAction,
} from "../../../../redux/Thunk/Product/ProductThunks";
import {
  setActiveForm,
  setActiveTab,
} from "../../../../redux/Reducer/Admin/PartnerSlice";
import apiAxios from "../../../../services/apiAxios";

const AssignedProductsDrawer = ({
  isOpen,
  onClose,
  drug,
  onAssignmentChange,
}) => {
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProductIIDs, setSelectedProductIIDs] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Search product to assign
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [searchedCatalogProducts, setSearchedCatalogProducts] = useState([]);
  const [isSearchingCatalog, setIsSearchingCatalog] = useState(false);
  const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);

  const loadProducts = useCallback(async () => {
    if (!drug?.id) return;
    setLoading(true);
    try {
      const res = await dispatch(fetchDrugProducts(drug.id)).unwrap();
      setProducts(res?.products || []);
    } catch (err) {
      console.error("Failed to load products:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, drug?.id]);

  useEffect(() => {
    if (isOpen && drug?.id) {
      loadProducts();
      setSelectedProductIIDs([]);
      setProductSearchQuery("");
      setSearchedCatalogProducts([]);
    }
  }, [isOpen, drug?.id, loadProducts]);

  // Debounced search for products to assign
  useEffect(() => {
    if (!productSearchQuery.trim()) {
      setSearchedCatalogProducts([]);
      setShowCatalogDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingCatalog(true);
      try {
        const response = await apiAxios.post("api/product/search-product", {
          search: productSearchQuery.trim(),
        });
        const results = Array.isArray(response?.data) ? response.data : [];
        // Filter out products that already have this drug assigned
        const filtered = results.filter(
          (p) =>
            !products.some(
              (assigned) => String(assigned.IID) === String(p.IID)
            )
        );
        setSearchedCatalogProducts(filtered);
        setShowCatalogDropdown(true);
      } catch (err) {
        console.error("Error searching products for assignment:", err);
      } finally {
        setIsSearchingCatalog(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchQuery, products]);

  const handleAssignProduct = async (productToAssign) => {
    if (!drug?.id || !productToAssign?.IID) return;
    setIsAssigning(true);
    try {
      await dispatch(
        assignDrugToProducts({
          drugId: drug.id,
          productIIDs: [productToAssign.IID],
        })
      ).unwrap();

      setProductSearchQuery("");
      setShowCatalogDropdown(false);
      await loadProducts();
      if (onAssignmentChange) onAssignmentChange();
    } catch (err) {
      console.error("Failed to assign product:", err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignSingle = async (product) => {
    if (!drug?.id || !product?.IID) return;
    if (
      !window.confirm(
        `Remove "${drug.drugName}" from product "${product.productName}" (#${product.IID})?`
      )
    ) {
      return;
    }

    setIsUnassigning(true);
    try {
      await dispatch(
        unassignDrugFromProducts({
          drugId: drug.id,
          productIIDs: [product.IID],
        })
      ).unwrap();

      setSelectedProductIIDs((prev) =>
        prev.filter((id) => id !== product.IID)
      );
      await loadProducts();
      if (onAssignmentChange) onAssignmentChange();
    } catch (err) {
      console.error("Failed to unassign product:", err);
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleBulkUnassign = async () => {
    if (!drug?.id || selectedProductIIDs.length === 0) return;
    if (
      !window.confirm(
        `Remove "${drug.drugName}" from ${selectedProductIIDs.length} selected product(s)?`
      )
    ) {
      return;
    }

    setIsUnassigning(true);
    try {
      await dispatch(
        unassignDrugFromProducts({
          drugId: drug.id,
          productIIDs: selectedProductIIDs,
        })
      ).unwrap();

      setSelectedProductIIDs([]);
      await loadProducts();
      if (onAssignmentChange) onAssignmentChange();
    } catch (err) {
      console.error("Failed to bulk unassign products:", err);
    } finally {
      setIsUnassigning(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedProductIIDs.length === products.length) {
      setSelectedProductIIDs([]);
    } else {
      setSelectedProductIIDs(products.map((p) => p.IID));
    }
  };

  const toggleSelectProduct = (iid) => {
    setSelectedProductIIDs((prev) =>
      prev.includes(iid) ? prev.filter((id) => id !== iid) : [...prev, iid]
    );
  };

  const handleNavigateToProduct = (product) => {
    const name = product?.productName || "";
    const IID = product?.IID || "";
    dispatch(setActiveForm("editProduct"));
    dispatch(setActiveTab({ name: "Products", index: 0 }));
    dispatch(searchProductsAction({ name, IID }));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-xs flex justify-end transition-opacity">
      <div
        className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col transform transition-transform animate-in slide-in-from-right duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50/80 to-white">
          <div className="min-w-0 pr-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-primary-50 text-primary-700 border border-primary-100">
                #{drug?.id}
              </span>
              <h2 className="text-lg font-black text-gray-900 truncate tracking-tight">
                {drug?.drugName}
              </h2>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-xs font-semibold text-gray-500">
                Assigned Products:{" "}
                <span className="text-primary-600 font-bold">
                  {products.length}
                </span>
              </span>
              {drug?.tags && drug.tags.length > 0 && (
                <div className="flex items-center gap-1">
                  {drug.tags.map((t, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white shadow-2xs"
                      style={{ backgroundColor: t.color || "#3B82F6" }}
                    >
                      {t.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer shrink-0"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Assign to New Product Bar */}
        <div className="p-4 bg-primary-50/40 border-b border-primary-100/60 relative">
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">
            + Assign Drug to Product Catalog
          </label>
          <div className="relative">
            <input
              type="text"
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              placeholder="Search product by name or IID (e.g. Crocin, Dolo, I00012)..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-800 placeholder:text-gray-400 focus:outline-hidden focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 shadow-2xs"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {isSearchingCatalog && (
              <div className="absolute right-3 top-2.5">
                <svg
                  className="animate-spin h-4 w-4 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Autocomplete Dropdown */}
          {showCatalogDropdown && (
            <div className="absolute left-4 right-4 top-full mt-1 bg-white rounded-2xl shadow-xl border border-gray-100 max-h-60 overflow-y-auto z-30 divide-y divide-gray-50">
              {searchedCatalogProducts.length === 0 ? (
                <div className="p-3 text-center text-xs text-gray-500">
                  {isSearchingCatalog
                    ? "Searching..."
                    : "No matching products found or already assigned."}
                </div>
              ) : (
                searchedCatalogProducts.map((p) => (
                  <div
                    key={p.IID || p.id}
                    className="p-3 hover:bg-primary-50/50 flex items-center justify-between transition-colors cursor-pointer"
                    onClick={() => handleAssignProduct(p)}
                  >
                    <div>
                      <p className="text-xs font-bold text-gray-900">
                        {p.productName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="font-mono text-[10px] text-gray-500 font-semibold">
                          #{p.IID}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {p.manufacturer || "Unknown Mfg"}
                        </span>
                        {p.type && (
                          <span className="px-1.5 py-0.2 bg-gray-100 text-gray-600 text-[9px] font-semibold rounded">
                            {p.type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-100">
                          MRP ₹{p.pricing?.mrp ?? 0}
                        </span>
                        <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-1.5 py-0.2 rounded border border-purple-200">
                          Discount: {(p.lookupMargin?.discount ?? p.pricing?.discount) ?? 0}%
                        </span>
                        {(p.lookupMargin?.type || (p.vId ? `V${p.vId}` : null)) && (
                          <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200 font-mono">
                            V ID: {p.lookupMargin?.type || `V${p.vId}`}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isAssigning}
                      className="px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-bold shadow-2xs transition-colors shrink-0"
                    >
                      + Assign
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Bulk Action Toolbar */}
        {selectedProductIIDs.length > 0 && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200/80 flex items-center justify-between">
            <span className="text-xs font-bold text-amber-900">
              {selectedProductIIDs.length} product(s) selected
            </span>
            <button
              type="button"
              disabled={isUnassigning}
              onClick={handleBulkUnassign}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg shadow-2xs transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Remove from Selected Products
            </button>
          </div>
        )}

        {/* Products List Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <svg
                className="animate-spin h-8 w-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              <span className="text-xs text-gray-500 font-medium">
                Loading assigned products...
              </span>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6 border-2 border-dashed border-gray-200 rounded-3xl bg-gray-50/50">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h4 className="text-sm font-bold text-gray-900">
                No Products Assigned
              </h4>
              <p className="text-xs text-gray-500 max-w-sm mt-1">
                This drug is not currently linked to any product in your inventory catalog. Use the search bar above to assign it.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      products.length > 0 &&
                      selectedProductIIDs.length === products.length
                    }
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-gray-600">
                    Select All ({products.length})
                  </span>
                </div>
                <span className="text-xs text-gray-400 font-medium">
                  Click product name to inspect details
                </span>
              </div>

              {products.map((prod) => {
                const isChecked = selectedProductIIDs.includes(prod.IID);
                const discountVal =
                  prod.lookupMargin?.discount !== undefined && prod.lookupMargin?.discount !== null
                    ? prod.lookupMargin.discount
                    : prod.pricing?.discount !== undefined && prod.pricing?.discount !== null
                    ? prod.pricing.discount
                    : prod.discount !== undefined && prod.discount !== null
                    ? prod.discount
                    : null;
                const vIdDisplay = prod.lookupMargin?.type || (prod.vId ? `V${prod.vId}` : null);

                return (
                  <div
                    key={prod.IID || prod.id}
                    className={`p-4 rounded-2xl border transition-all duration-200 flex items-start gap-3.5 ${
                      isChecked
                        ? "bg-primary-50/40 border-primary-200 shadow-xs"
                        : "bg-white border-gray-100 hover:border-gray-200 hover:shadow-xs"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelectProduct(prod.IID)}
                      className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300 mt-1 cursor-pointer"
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => handleNavigateToProduct(prod)}
                          className="text-left font-bold text-sm text-gray-900 hover:text-primary-600 hover:underline truncate group inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                          title="Open product in Products tab"
                        >
                          <span className="truncate">{prod.productName}</span>
                          <svg
                            className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-0.5 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                            />
                          </svg>
                        </button>
                        <span className="font-mono text-[11px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-md border border-gray-200 shrink-0">
                          #{prod.IID}
                        </span>
                      </div>

                      {/* Line 1: Manufacturer, Type, Stock */}
                      <div className="flex items-center gap-2.5 mt-1.5 flex-wrap text-xs">
                        <span className="text-gray-500 font-medium">
                          {prod.manufacturer || "Manufacturer: N/A"}
                        </span>
                        {prod.type && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-semibold rounded-md border border-blue-100">
                            {prod.type}
                          </span>
                        )}
                        <span className="font-bold text-gray-800">
                          Stock: {prod.productStock ?? 0}
                          {prod.uom ? ` (${prod.uom})` : ""}
                        </span>
                      </div>

                      {/* Line 2: MRP, Discount, V ID in symmetry */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 shadow-2xs">
                          MRP ₹{prod.pricing?.mrp ?? 0}
                        </span>
                        <span className="text-xs font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-200 shadow-2xs">
                          Discount: {discountVal !== null && discountVal !== undefined ? `${discountVal}%` : "0%"}
                        </span>
                        <span className="text-xs font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 font-mono shadow-2xs">
                          V ID: {vIdDisplay || (prod.vId ? `V${prod.vId}` : "N/A")}
                        </span>
                      </div>

                      {/* Drugs Composition Chips */}
                      {prod.drugs && prod.drugs.length > 1 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          <span className="text-[10px] text-gray-400 font-bold uppercase">
                            Full Formula:
                          </span>
                          {prod.drugs.map((d, dIdx) => (
                            <span
                              key={dIdx}
                              className={`px-1.5 py-0.2 text-[10px] rounded font-medium ${
                                d.toLowerCase() ===
                                drug?.drugName?.toLowerCase()
                                  ? "bg-primary-100 text-primary-800 font-bold"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Row Action */}
                    <div className="flex items-center gap-1 shrink-0 self-center">
                      <button
                        type="button"
                        onClick={() => handleUnassignSingle(prod)}
                        title="Unassign this drug from product"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
          <span className="text-xs text-gray-500 font-medium">
            Changes to product composition are applied immediately.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignedProductsDrawer;
